import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Baby, Edit, Users, Phone, Mail, Calendar, Shield } from 'lucide-react';
import { parentManagement, parentAssignments } from '../services/api';

const ParentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchParentDetails();
  }, [id]);

  const fetchParentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch parent details
      const parentResponse = await parentManagement.getParentById(id);
      setParent(parentResponse.data.data);

      // Fetch parent's children assignments
      const assignmentsResponse = await parentAssignments.getByParent(id);
      setAssignments(assignmentsResponse.data.data);

    } catch (error) {
      console.error('Error fetching parent details:', error);
      setError('Falha ao carregar detalhes do responsável');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      default:
        return status;
    }
  };

  const getRelationshipText = (relationship) => {
    switch (relationship) {
      case 'mother':
        return 'Mãe';
      case 'father':
        return 'Pai';
      case 'guardian':
        return 'Responsável';
      case 'grandmother':
        return 'Avó';
      case 'grandfather':
        return 'Avô';
      default:
        return relationship;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-800 border border-red-200 rounded-lg p-4">
          {error}
        </div>
      </div>
    );
  }

  if (!parent) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg p-4">
          Responsável não encontrado
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/parents')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detalhes do Responsável</h1>
            <p className="text-gray-600">Informações completas do responsável e suas crianças</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/dashboard/parents/${id}/edit`)}
            className="bg-brand-700 text-white px-4 py-2 rounded-lg hover:bg-brand-800 flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Editar
          </button>
          <button
            onClick={() => navigate(`/dashboard/parents/${id}/assignments`)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Gerenciar Crianças
          </button>
        </div>
      </div>

      {/* Parent Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5" />
            Informações do Responsável
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Nome Completo</h3>
              <p className="text-lg text-gray-900">{parent.firstName} {parent.lastName}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Email</h3>
              <p className="text-lg text-gray-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                {parent.email}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Telefone</h3>
              <p className="text-lg text-gray-900 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                {parent.phoneNumber || 'Não informado'}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(parent.status)}`}>
                {getStatusText(parent.status)}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">ID do Usuário</h3>
              <p className="text-sm text-gray-600 font-mono">{parent.id}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Último Login</h3>
              <p className="text-lg text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {parent.lastLogin 
                  ? new Date(parent.lastLogin).toLocaleDateString('pt-BR')
                  : 'Nunca'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Children Assignments */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Baby className="w-5 h-5" />
            Crianças Vinculadas ({assignments.length})
          </h2>
        </div>
        
        {assignments.length === 0 ? (
          <div className="p-8 text-center">
            <Baby className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma criança vinculada</h3>
            <p className="text-gray-600 mb-4">
              Este responsável ainda não tem crianças vinculadas
            </p>
            <button
              onClick={() => navigate(`/dashboard/parents/${id}/assignments`)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Vincular Primeira Criança
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criança
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Idade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Relacionamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Principal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data de Vínculo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignments.map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Baby className="w-5 h-5 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {assignment.patient?.firstName} {assignment.patient?.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {assignment.patient?.patientId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calculateAge(assignment.patient?.dateOfBirth)} anos
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-brand-100 text-brand-800">
                        {getRelationshipText(assignment.relationship)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {assignment.isPrimary ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Sim
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Não
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(assignment.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDetails; 